import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PortalShell } from "@/components/portal/PortalShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";

const NAV = [
  { to: "/app/crm", label: "Übersicht" },
  { to: "/app/crm/customers", label: "Kunden" },
  { to: "/app/crm/leads", label: "Leads" },
  { to: "/app/crm/projects", label: "Projekte" },
];

export const Route = createFileRoute("/_authenticated/app/crm/customers")({
  ssr: false,
  head: () => ({ meta: [{ title: "Kunden" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: CustomersPage,
});

function CustomersPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    type: "private", first_name: "", last_name: "", company: "", email: "", phone: "", city: "",
  });
  const [err, setErr] = useState<string | null>(null);

  const { data: customers, isLoading } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customers")
        .select("id, customer_no, type, first_name, last_name, company, email, city, status, created_at")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return data;
    },
  });

  const createMut = useMutation({
    mutationFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      const { data: prof } = await supabase.from("profiles").select("tenant_id").eq("id", u.user!.id).single();
      const { error } = await supabase.from("customers").insert({
        tenant_id: prof!.tenant_id,
        owner_user_id: u.user!.id,
        ...form,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      setForm({ type: "private", first_name: "", last_name: "", company: "", email: "", phone: "", city: "" });
      setErr(null);
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["crm-counts"] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  function onSubmit(e: FormEvent) { e.preventDefault(); createMut.mutate(); }

  return (
    <PortalShell nav={NAV}>
      <h1 className="text-2xl font-semibold mb-6">Kunden</h1>

      <Card className="mb-6">
        <CardHeader><CardTitle className="text-base">Neuen Kunden anlegen</CardTitle></CardHeader>
        <CardContent>
          {err && <Alert variant="destructive" className="mb-4"><AlertDescription>{err}</AlertDescription></Alert>}
          <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Typ</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Privat</SelectItem>
                  <SelectItem value="business">Geschäftlich</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Vorname</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
            <div className="space-y-1"><Label>Nachname</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
            {form.type === "business" && (
              <div className="space-y-1 sm:col-span-3"><Label>Firma</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
            )}
            <div className="space-y-1"><Label>E-Mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-1"><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="space-y-1"><Label>Ort</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div className="sm:col-span-3"><Button type="submit" disabled={createMut.isPending}>{createMut.isPending ? "…" : "Kunde anlegen"}</Button></div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Alle Kunden</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? <p className="text-sm text-muted-foreground">Lade …</p> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Nr.</TableHead><TableHead>Name</TableHead><TableHead>Typ</TableHead>
                <TableHead>E-Mail</TableHead><TableHead>Ort</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(customers ?? []).map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-mono text-xs">
                      <Link to="/app/crm/customers/$id" params={{ id: c.id }} className="underline">{c.customer_no}</Link>
                    </TableCell>
                    <TableCell>{c.company || `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() || "—"}</TableCell>
                    <TableCell>{c.type === "business" ? "Geschäftlich" : "Privat"}</TableCell>
                    <TableCell>{c.email ?? "—"}</TableCell>
                    <TableCell>{c.city ?? "—"}</TableCell>
                    <TableCell>{c.status}</TableCell>
                  </TableRow>
                ))}
                {(customers ?? []).length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Noch keine Kunden.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </PortalShell>
  );
}
